import React from 'react'
import { Download, FileText, FileJson, FileSpreadsheet } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { exportToCSV, exportToJSON, exportToMarkdown } from '@/utils/exportUtils'
import type { EvaluationResult, TestSetWithDetails } from '@shared/types/evaluation'
import { toast } from 'sonner'

interface ExportButtonProps {
  testSet: TestSetWithDetails
  results: EvaluationResult[]
  disabled?: boolean
}

export const ExportButton: React.FC<ExportButtonProps> = ({ 
  testSet, 
  results, 
  disabled = false 
}) => {
  const handleExport = (format: 'csv' | 'json' | 'markdown') => {
    if (results.length === 0) {
      toast.error('No results to export')
      return
    }

    const exportDate = new Date().toISOString().split('T')[0]
    const exportData = {
      testSet,
      results,
      exportDate
    }

    try {
      switch (format) {
        case 'csv':
          exportToCSV(exportData)
          toast.success('Results exported as CSV')
          break
        case 'json':
          exportToJSON(exportData)
          toast.success('Results exported as JSON')
          break
        case 'markdown':
          exportToMarkdown(exportData)
          toast.success('Results exported as Markdown')
          break
        default:
          toast.error('Unknown export format')
      }
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export results')
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="gap-2" 
          disabled={disabled || results.length === 0}
        >
          <Download className="h-4 w-4" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport('csv')}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('json')}>
          <FileJson className="h-4 w-4 mr-2" />
          Export as JSON
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('markdown')}>
          <FileText className="h-4 w-4 mr-2" />
          Export as Markdown
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}